import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, AlertCircle, Crown, ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const TrialBanner = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/api/user/subscription-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setSubscriptionInfo(data);

      // Calculate days remaining if on trial
      if (data.is_trial && data.trial_ends) {
        const trialEnd = new Date(data.trial_ends);
        const now = new Date();
        const diffTime = trialEnd - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysRemaining(diffDays > 0 ? diffDays : 0);
      }
    } catch (error) {
      console.error('Error loading subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  // Don't show banner if loading, not on trial, or admin
  if (loading || !subscriptionInfo || !subscriptionInfo.is_trial || subscriptionInfo.is_admin) {
    return null;
  }

  // Don't show if trial has expired (will be handled by backend)
  if (daysRemaining === null || daysRemaining < 0) {
    return null;
  }

  // Determine banner style based on days remaining
  const getAlertVariant = () => {
    if (daysRemaining <= 3) return 'destructive';
    if (daysRemaining <= 7) return 'warning';
    return 'info';
  };

  const getBannerColor = () => {
    if (daysRemaining <= 3) return 'bg-red-50 border-red-200';
    if (daysRemaining <= 7) return 'bg-yellow-50 border-yellow-200';
    return 'bg-blue-50 border-blue-200';
  };

  const getIconColor = () => {
    if (daysRemaining <= 3) return 'text-red-600';
    if (daysRemaining <= 7) return 'text-yellow-600';
    return 'text-blue-600';
  };

  return (
    <Alert className={`mb-6 ${getBannerColor()} border-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3 flex-1">
          {daysRemaining <= 7 ? (
            <AlertCircle className={`h-5 w-5 mt-0.5 ${getIconColor()}`} />
          ) : (
            <Clock className={`h-5 w-5 mt-0.5 ${getIconColor()}`} />
          )}
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={`${getIconColor()} border-current`}>
                Free Trial
              </Badge>
              <Badge variant="secondary">
                {subscriptionInfo.tier_display || subscriptionInfo.tier_name}
              </Badge>
            </div>
            
            <AlertDescription className="text-sm font-medium">
              {daysRemaining === 0 ? (
                <span className="text-red-700">
                  Your trial expires today! Upgrade now to continue using all features.
                </span>
              ) : daysRemaining === 1 ? (
                <span className={daysRemaining <= 3 ? 'text-red-700' : 'text-gray-700'}>
                  Your trial expires in <span className="font-bold">{daysRemaining} day</span>. 
                  {daysRemaining <= 3 && ' Upgrade now to avoid losing access!'}
                </span>
              ) : (
                <span className={daysRemaining <= 3 ? 'text-red-700' : 'text-gray-700'}>
                  Your trial expires in <span className="font-bold">{daysRemaining} days</span>. 
                  {daysRemaining <= 7 && ' Consider upgrading to keep full access!'}
                </span>
              )}
            </AlertDescription>
            
            <p className="text-xs text-gray-600 mt-1">
              After trial ends, you'll be downgraded to the Starter (Free) plan.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <Button 
            variant={daysRemaining <= 3 ? "default" : "outline"}
            size="sm"
            onClick={handleUpgrade}
            className="whitespace-nowrap"
          >
            <Crown className="h-4 w-4 mr-2" />
            Upgrade Now
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default TrialBanner;
